import { ReactNode } from "react";
import { Box } from "@mui/material";
import { DotLegalButton, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import PaneLayoutControls from "./PaneLayoutControls";
import { usePane } from "./usePane";
import { usePaneLayoutStyles } from "./PaneLayout.styles";

export interface PaneLayoutProps {
    /** A string is drawn as the pane's title; anything else is drawn as it is, for a pane with a title of its own. */
    header: string | ReactNode;
    /** What the pane puts beside the controls, such as an overflow menu. */
    actions?: ReactNode;
    /** Draws the primary button. Left out, the pane has no primary button. */
    okButtonText?: string;
    onOkClick?: () => void;
    okButtonDisabled?: boolean;
    inProgress?: boolean;
    /** Defaults to "cancel" beside a primary button. Set on its own for a pane with a secondary button only. */
    secondaryButtonText?: string;
    /** Defaults to closing the pane, asking first when there is something to lose. */
    onSecondaryButtonClick?: () => void;
    /** Drawn at the start of the button row, away from the buttons, such as a reset link. */
    footerStart?: ReactNode;
    /** For a pane whose footer is not a row of buttons. Replaces the buttons above. */
    footer?: ReactNode;
    /** Text that stays in view under the buttons, such as an AI disclaimer. */
    footerNote?: ReactNode;
    /** For a pane that fills its own edges, such as a document viewer. */
    noContentPadding?: boolean;
    /**
     * Field tooltips are absolutely positioned to the right of their input, outside it, so the content inset
     * alone lets the pane edge cut them off. A pane with fields that can carry a tooltip, including template
     * fields whose tooltip comes from the customer's own template, reserves the room for them with this.
     */
    reserveFieldTooltipSpace?: boolean;
    /**
     * Short content that stays in view above the scrolling content, such as column headings. The content
     * scrolls in the layout, so a pane never needs a scroller of its own to keep something pinned.
     */
    fixedContent?: ReactNode;
    children: ReactNode;
}

/** A pane's title, drawn like the header of a dialog so the rail and the dialogs read as one kind of window. */
export function PaneTitle({ children }: { children: ReactNode }) {
    const styles = usePaneLayoutStyles();

    return (
        <Box sx={styles.paneTitle}>
            <DotLegalHeader headerStyle="small" marginBottom={0}>
                {children}
            </DotLegalHeader>
        </Box>
    );
}

/**
 * Header, content and footer for every pane. The collapse and close controls, the divider beneath them and
 * the content inset belong to the layout rather than to the pane, so a pane can neither suppress nor
 * reposition them.
 */
function PaneLayout(props: PaneLayoutProps) {
    const { translateString } = useTranslation();
    const styles = usePaneLayoutStyles();
    const { requestClose } = usePane();

    const secondaryButtonText = props.secondaryButtonText ?? (props.okButtonText ? translateString("cancel") : undefined);
    const hasButtons = props.okButtonText !== undefined || secondaryButtonText !== undefined;
    const standardButtons = hasButtons && (
        <>
            {secondaryButtonText && (
                <DotLegalButton buttonType="secondary" size="small" onClick={props.onSecondaryButtonClick ?? requestClose}>
                    {secondaryButtonText}
                </DotLegalButton>
            )}
            {props.okButtonText && (
                <DotLegalButton buttonType="primary" size="small" onClick={props.onOkClick} disabled={props.okButtonDisabled} isLoading={props.inProgress}>
                    {props.okButtonText}
                </DotLegalButton>
            )}
        </>
    );
    const footerButtons = props.footer ?? standardButtons;

    return (
        <>
            <Box sx={styles.header}>
                <Box sx={styles.headerTitle}>{typeof props.header === "string" ? <PaneTitle>{props.header}</PaneTitle> : props.header}</Box>

                <Box sx={styles.headerActions}>
                    {props.actions}
                    <PaneLayoutControls />
                </Box>
            </Box>

            {props.fixedContent && <Box sx={styles.fixedContent}>{props.fixedContent}</Box>}

            <Box
                sx={{
                    ...(props.noContentPadding ? styles.content : styles.contentPadded),
                    ...(props.fixedContent ? styles.contentBesideFixedContent : {}),
                }}
            >
                {props.reserveFieldTooltipSpace ? <Box sx={styles.fieldTooltipSpace}>{props.children}</Box> : props.children}
            </Box>

            {(footerButtons || props.footerNote) && (
                <Box sx={styles.footer}>
                    {footerButtons && (
                        <Box sx={styles.footerButtons}>
                            {props.footerStart && <Box sx={styles.footerStart}>{props.footerStart}</Box>}
                            {footerButtons}
                        </Box>
                    )}
                    {props.footerNote && <Box sx={styles.footerNote}>{props.footerNote}</Box>}
                </Box>
            )}
        </>
    );
}

export default PaneLayout;
